import { authFetch } from '../api/client';

const SEAWALL_API = import.meta.env.VITE_SEAWALL_API_URL ?? '/seawall';

export type SensorValueType = 'int' | 'float' | 'char' | 'media';
export type ArraySpecType = 'media' | 'data' | 'multi';

export type ApiReadLabel = {
  ID: number | string;
  readValLabel: string;
  unit: string;
  unitAbbreviation: string;
};

export type ApiSensorSpec = {
  ID: number | string;
  type: SensorValueType;
  name: number | string;
  version: number;
  'specification_read$labelID'?: number | string;
  defineDateTime?: string;
};

export type ApiArraySpec = {
  ID: number | string;
  type: ArraySpecType | string;
  name: number | string;
  version: number;
  defineDateTime?: string;
};

export type ApiSensorArray = {
  ID: number | string;
  'sensor$array_specificationID'?: number | string;
  sensorArraySpecificationID?: number | string;
  boxID: number | string | null;
  readKey?: string;
  assembleDateTime?: string;
};

export type ApiSensor = {
  ID: number | string;
  'sensor$arrayID'?: number | string;
  sensorArrayID?: number | string;
  sensor_specificationID?: number | string;
  addDateTime?: string;
  serialNumber?: string | null;
};

export type EnrichedSensorSpec = {
  id: string;
  type: SensorValueType;
  version: number;
  labelId: string | null;
  readValLabel: string;
  unit: string;
  unitAbbreviation: string;
};

export type ChosenSensorSpec = EnrichedSensorSpec & {
  key: string;
  createdNow: boolean;
};

export type ArraySpecCard = {
  id: string;
  type: string;
  name: string;
  version: number;
  sensors: EnrichedSensorSpec[];
  physicalSensorCount: number;
};

export type NewArrayAssembly = {
  arraySpecification: {
    type: ArraySpecType;
    version: number;
    sensorSpecificationIDs: string[];
  };
  sensorArray: {
    boxID: string;
    sensorArraySpecificationID: string;
  };
  sensors: Array<{
    sensor_specificationID: string;
    sensorArrayID: string;
  }>;
};

export class ArrayAssemblyUnavailableError extends Error {
  constructor(public readonly payload: NewArrayAssembly) {
    super(
      'Creating a sensor array is not available yet. The Seawall API has not documented POST routes for array specifications, arrays, or sensors.',
    );
    this.name = 'ArrayAssemblyUnavailableError';
  }
}

function asList<T>(data: unknown): T[] {
  if (Array.isArray(data)) return data as T[];
  if (data && typeof data === 'object') {
    const record = data as { rows?: T[] };
    if (Array.isArray(record.rows)) return record.rows;
  }
  return [];
}

async function fetchJson(path: string): Promise<unknown> {
  const response = await fetch(`${SEAWALL_API}${path}`);
  if (!response.ok) {
    throw new Error(`${path} responded with ${response.status}`);
  }
  return response.json();
}

export function arraySpecIdOf(array: ApiSensorArray): string | null {
  const raw = array['sensor$array_specificationID'] ?? array.sensorArraySpecificationID;
  return raw === undefined || raw === null ? null : String(raw);
}

export function sensorArrayIdOf(sensor: ApiSensor): string | null {
  const raw = sensor['sensor$arrayID'] ?? sensor.sensorArrayID;
  return raw === undefined || raw === null ? null : String(raw);
}

export function enrichSensorSpec(
  spec: ApiSensorSpec,
  labels: ApiReadLabel[],
): EnrichedSensorSpec {
  const labelId =
    spec['specification_read$labelID'] === undefined || spec['specification_read$labelID'] === null
      ? null
      : String(spec['specification_read$labelID']);
  const label = labels.find((item) => String(item.ID) === labelId);
  return {
    id: String(spec.ID),
    type: spec.type,
    version: spec.version,
    labelId,
    readValLabel: label?.readValLabel ?? `Label ${labelId ?? 'unknown'}`,
    unit: label?.unit ?? '',
    unitAbbreviation: label?.unitAbbreviation ?? '',
  };
}

export function specsMatchingLabel(
  specs: EnrichedSensorSpec[],
  readValLabel: string,
): EnrichedSensorSpec[] {
  const needle = readValLabel.trim().toLowerCase();
  if (!needle) return [];
  return specs.filter((spec) => spec.readValLabel.trim().toLowerCase() === needle);
}

export function inferArraySpecType(sensors: Array<{ type: SensorValueType }>): ArraySpecType {
  if (sensors.length === 0) return 'data';
  const allMedia = sensors.every((sensor) => sensor.type === 'media');
  if (allMedia) return 'media';
  const hasMedia = sensors.some((sensor) => sensor.type === 'media');
  const hasData = sensors.some((sensor) => sensor.type !== 'media');
  if (hasMedia && hasData) return 'multi';
  return 'data';
}

export function buildArraySpecCards(
  arraySpecs: ApiArraySpec[],
  arrays: ApiSensorArray[],
  sensors: ApiSensor[],
  specs: EnrichedSensorSpec[],
): ArraySpecCard[] {
  const specById = new Map(specs.map((spec) => [spec.id, spec]));

  return arraySpecs.map((arraySpec) => {
    const id = String(arraySpec.ID);
    const arraysUsingSpec = arrays.filter((array) => arraySpecIdOf(array) === id);
    const arrayIds = new Set(arraysUsingSpec.map((array) => String(array.ID)));
    const sensorsOnSpec = sensors.filter((sensor) => {
      const arrayId = sensorArrayIdOf(sensor);
      return arrayId !== null && arrayIds.has(arrayId);
    });

    const unique = new Map<string, EnrichedSensorSpec>();
    for (const sensor of sensorsOnSpec) {
      const specId =
        sensor.sensor_specificationID === undefined || sensor.sensor_specificationID === null
          ? null
          : String(sensor.sensor_specificationID);
      if (!specId || unique.has(specId)) continue;
      const spec = specById.get(specId);
      if (spec) unique.set(specId, spec);
    }

    return {
      id,
      type: String(arraySpec.type),
      name: String(arraySpec.name),
      version: arraySpec.version,
      sensors: [...unique.values()],
      physicalSensorCount: sensorsOnSpec.length,
    };
  });
}

export type ArrayWorkspace = {
  labels: ApiReadLabel[];
  sensorSpecs: EnrichedSensorSpec[];
  arraySpecCards: ArraySpecCard[];
};

export async function loadArrayWorkspace(): Promise<ArrayWorkspace> {
  const [specsData, labelsData, arraySpecsData, arraysData, sensorsData] = await Promise.all([
    fetchJson('/sensorSpecifications/'),
    fetchJson('/sensorSpecifications/labels'),
    fetchJson('/sensorArraySpecifications/'),
    fetchJson('/sensorArrays/'),
    fetchJson('/sensors/'),
  ]);

  const labels = asList<ApiReadLabel>(labelsData);
  const sensorSpecs = asList<ApiSensorSpec>(specsData).map((spec) => enrichSensorSpec(spec, labels));
  const arraySpecCards = buildArraySpecCards(
    asList<ApiArraySpec>(arraySpecsData),
    asList<ApiSensorArray>(arraysData),
    asList<ApiSensor>(sensorsData),
    sensorSpecs,
  );

  return { labels, sensorSpecs, arraySpecCards };
}

async function parseApiError(res: Response): Promise<string> {
  try {
    const data = await res.json();
    if (typeof data?.message === 'string') return data.message;
  } catch {
    /* empty body */
  }
  return `Request failed (${res.status})`;
}

export type CreateSensorSpecInput = {
  type: SensorValueType;
  version: number;
  readValLabelID?: number;
  readValLabelObj?: {
    readValLabel: string;
    unit: string;
    unitAbbreviation: string;
  };
};

export async function createSensorSpecification(input: CreateSensorSpecInput): Promise<string> {
  const body: Record<string, unknown> = {
    type: input.type,
    version: input.version,
  };
  if (input.readValLabelID !== undefined) {
    body.readValLabelID = input.readValLabelID;
  } else if (input.readValLabelObj) {
    body.readValLabelObj = input.readValLabelObj;
  }

  const res = await authFetch(`${SEAWALL_API}/sensorSpecifications/`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });

  if (!res.ok) throw new Error(await parseApiError(res));

  const data = (await res.json()) as { newSensorSpecID?: string | number };
  if (data.newSensorSpecID === undefined || data.newSensorSpecID === null) {
    throw new Error('Sensor specification was created but no ID was returned.');
  }
  return String(data.newSensorSpecID);
}

export type CreatedSensorRow = {
  id: string;
  type: SensorValueType;
  readKey: string;
  unit: string;
  readValLabel: string;
  unitAbbreviation: string;
};

export type ArrayAssemblyResult = {
  sensorArrayID: string;
  readKey: string;
  sensors: CreatedSensorRow[];
};

function asNumericId(id: string, label: string): number {
  const value = Number(id);
  if (!Number.isFinite(value)) {
    throw new Error(`${label} "${id}" is not a valid numeric ID.`);
  }
  return value;
}

function firstString(record: Record<string, unknown>, keys: string[]): string | null {
  for (const key of keys) {
    const value = record[key];
    if (value !== undefined && value !== null && String(value).length > 0) {
      return String(value);
    }
  }
  return null;
}

function asRecord(data: unknown): Record<string, unknown> {
  return data && typeof data === 'object' ? (data as Record<string, unknown>) : {};
}

async function postJson(path: string, body: Record<string, unknown>): Promise<{ status: number; data: unknown }> {
  const res = await authFetch(`${SEAWALL_API}${path}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });

  let data: unknown = null;
  try {
    data = await res.json();
  } catch {
    data = null;
  }

  return { status: res.status, data };
}

function isMissingRoute(status: number): boolean {
  return status === 404 || status === 405;
}

function errorMessageFrom(data: unknown, status: number): string {
  const record = asRecord(data);
  if (typeof record.message === 'string') return record.message;
  if (typeof record.dbmessage === 'string') return record.dbmessage;
  return `Request failed (${status})`;
}

export async function assembleSensorArray(
  boxID: string,
  arraySpec: { id?: string; type: ArraySpecType; version: number },
  sensors: EnrichedSensorSpec[],
): Promise<ArrayAssemblyResult> {
  const sensorSpecIds = sensors.map((sensor) => sensor.id);
  const planned: NewArrayAssembly = {
    arraySpecification: {
      type: arraySpec.type,
      version: arraySpec.version,
      sensorSpecificationIDs: sensorSpecIds,
    },
    sensorArray: {
      boxID,
      sensorArraySpecificationID: arraySpec.id ?? '<new>',
    },
    sensors: sensorSpecIds.map((specId) => ({
      sensor_specificationID: specId,
      sensorArrayID: '<pending>',
    })),
  };

  let arraySpecId = arraySpec.id ?? null;

  if (!arraySpecId) {
    const createdSpec = await postJson('/sensorArraySpecifications/', {
      type: arraySpec.type,
      version: arraySpec.version,
      sensorSpecificationIDs: sensorSpecIds.map((id) => asNumericId(id, 'sensor specification ID')),
    });

    if (isMissingRoute(createdSpec.status)) {
      throw new ArrayAssemblyUnavailableError(planned);
    }
    if (createdSpec.status < 200 || createdSpec.status >= 300) {
      throw new Error(`Creating the array specification failed: ${errorMessageFrom(createdSpec.data, createdSpec.status)}`);
    }

    arraySpecId = firstString(asRecord(createdSpec.data), [
      'newSensorArraySpecID',
      'newArraySpecID',
      'newSensor$array_specificationID',
      'ID',
      'id',
    ]);
    if (!arraySpecId) {
      throw new Error('Array specification was created but no ID was returned.');
    }
  }

  planned.sensorArray.sensorArraySpecificationID = arraySpecId;

  const createdArray = await postJson('/sensorArrays/', {
    boxID: asNumericId(boxID, 'box ID'),
    'sensor$array_specificationID': asNumericId(arraySpecId, 'array specification ID'),
  });

  if (isMissingRoute(createdArray.status)) {
    throw new ArrayAssemblyUnavailableError(planned);
  }
  if (createdArray.status < 200 || createdArray.status >= 300) {
    throw new Error(
      `Creating the sensor array failed${arraySpec.id ? '' : ` (array spec ID ${arraySpecId} was created)`}: ${errorMessageFrom(createdArray.data, createdArray.status)}`,
    );
  }

  const arrayRecord = asRecord(createdArray.data);
  const sensorArrayID = firstString(arrayRecord, [
    'newSensorArrayID',
    'newSensor$arrayID',
    'sensorArrayID',
    'ID',
    'id',
  ]);
  const readKey = firstString(arrayRecord, ['readKey', 'readkey', 'newReadKey']) ?? '';
  if (!sensorArrayID) {
    throw new Error('Sensor array was created but no ID was returned.');
  }

  const createdSensors: CreatedSensorRow[] = [];
  for (const spec of sensors) {
    const createdSensor = await postJson('/sensors/', {
      'sensor$arrayID': asNumericId(sensorArrayID, 'sensor array ID'),
      sensor_specificationID: asNumericId(spec.id, 'sensor specification ID'),
    });

    if (isMissingRoute(createdSensor.status)) {
      throw new ArrayAssemblyUnavailableError({
        ...planned,
        sensors: planned.sensors.map((row) => ({ ...row, sensorArrayID })),
      });
    }
    if (createdSensor.status < 200 || createdSensor.status >= 300) {
      throw new Error(
        `Creating sensor for spec ${spec.id} failed (array ID ${sensorArrayID}): ${errorMessageFrom(createdSensor.data, createdSensor.status)}`,
      );
    }

    const sensorId =
      firstString(asRecord(createdSensor.data), ['newSensorID', 'sensorID', 'ID', 'id']) ?? '';
    if (!sensorId) {
      throw new Error(`Sensor for spec ${spec.id} was created but no ID was returned.`);
    }

    createdSensors.push({
      id: sensorId,
      type: spec.type,
      readKey,
      unit: spec.unit,
      readValLabel: spec.readValLabel,
      unitAbbreviation: spec.unitAbbreviation,
    });
  }

  return { sensorArrayID, readKey, sensors: createdSensors };
}
